import { confirmUserAttribute, fetchAuthSession, signOut, fetchUserAttributes, getCurrentUser } from "aws-amplify/auth"
import { useClientStore } from '@/stores/client';
import { useCognitoStore } from '@/stores/cognito';
import { useStoreClientGroup } from "@/stores/storeClientGroup";
import { useStoreCognitoUserAttributes } from '@/stores/storeCognitoUserAttributes';
import { useDateOfBirthVerificationStatusStore } from '@/stores/dateOfBirthVerificationStatus';
import { getActivePinia } from "pinia";
import { provideApolloClient, useMutation } from '@vue/apollo-composable'
import { apolloClient } from "@/utils/apolloClient";
import Swal from 'sweetalert2';
import { APP } from "@/config/app";
import router from "@/router";
import { gqlUpdateClientPreferences } from "@/graphql/clients";
import { gqlGetClientEmailAndExternalReference } from "@/graphql/clients";

provideApolloClient(apolloClient);

export async function storeCurrentSession(refresh = false) {
    const storeCognito = useCognitoStore();
    const storeClient = useClientStore();
    try {
        const response = await fetchAuthSession({ forceRefresh: refresh });

        if (response.tokens) {
            storeCognito.accessToken = response.tokens.accessToken.toString();
            storeCognito.idToken = response.tokens.idToken.toString();
            await getUserAttributes();
            storeCognito.authenticated = true;

            const autoLogoutLength = parseInt(import.meta.env.VITE_TIMEOUT)
            storeCognito.timeout = Date.now();

            setInterval(async function () {
                if (storeCognito.timeout !== '' && Date.now() > storeCognito.timeout + autoLogoutLength) {
                    await handleSignOut()

                    await router.push({
                        name: "Login",
                        query: {
                            timedOut: true
                        },
                    });
                }
            }, autoLogoutLength);

        
            storeClient.email = await getCurrentUser().then(user => user?.signInDetails?.loginId || null);
            // TODO: In the next version we will allow computing the app config values by external ref once they are available after KYC
            // emailExtRefData = await apolloClient.query({
            //     query: gqlGetClientEmailAndExternalReference,
            //     // We can depending on this being cached for when this function is called multiple times when rendering a page
            //     fetchPolicy: 'cache-first',
            //     variables: {_: storeCognito.getIdToken}
            // });
            // if (emailExtRefData?.data != null) {
            //     storeClient.email = emailExtRefData?.data?.clientInfo?.email || null;
            //     storeClient.externalReference = emailExtRefData?.data?.clientInfo?.externalReference || null;
            // }
        } else {
            storeCognito.authenticated = false;
        }
    } catch (err) {
        console.log(err);
        storeCognito.authenticated = false;
    }
}
export async function handleSignOut() {
    const storeDateOfBirthVerificationStatus = useDateOfBirthVerificationStatusStore();
    storeDateOfBirthVerificationStatus.verificationRequired = true;
    storeDateOfBirthVerificationStatus.verified = false;

    try {
        await signOut();
        // Clear Pinia stores in memory
        getActivePinia()._s.forEach(store => store.$reset());

        const showZeroBalancedPortfolios = localStorage.getItem('showZeroBalancedPortfolios')

        localStorage.clear();

        if (showZeroBalancedPortfolios !== null) {
            localStorage.setItem('showZeroBalancedPortfolios', showZeroBalancedPortfolios);
        }

        // empty the GraphQL cache
        await apolloClient.cache.reset();

        const storeCognito = useCognitoStore();
        storeCognito.authenticated = false;
    } catch (error) {
        console.log('error signing out: ', error);
    }
}

export async function getUserAttributes() {
    try {
        await fetchUserAttributes().then((result) => {
            // save the client_group
            //const storeCognito = useCognitoStore();
            //storeCognito.clientGroup = result['custom:client_group'];
            const clientGroupStore = useStoreClientGroup();
            clientGroupStore.clientGroup = result['custom:client_group'];

            // save the phone number for optional 2FA
            const storeCognitoUserAttributes = useStoreCognitoUserAttributes();
            storeCognitoUserAttributes.phoneNumber = result['phone_number'];

            return result;
        });
    } catch (error) {
        console.log('error: ', error);
    }
}

export async function handleConfirmUserAttribute({
    userAttributeKey,
    confirmationCode
}) {
    try {
        return await confirmUserAttribute({userAttributeKey, confirmationCode});
    } catch (error) {
        // Exceptions thrown:
        // -AuthValidationErrorCode - Thrown when confirmationCode is not defined.
        // -VerifyUserAttributeException - Thrown due to an invalid confirmation code or attribute.
        // -AuthTokenConfigException - Thrown when the token provider config is invalid.

        console.log(error);
        throw new Error(error);
    }
}

export const updateClientPreferences = async (type, value, silent = null) => {

    value = value === true || value === 'true';

    const {
        mutate: updateClientPreferencesMutation,
        loading: updateClientPreferencesLoading,
        error: updateClientPreferencesError
    } = useMutation(gqlUpdateClientPreferences);

    const updateClientPreferencesResponse = await updateClientPreferencesMutation({
        preferenceType: type,
        value: value
    });

    if (updateClientPreferencesError.value) {
        graphQLError.value = updateClientPreferencesError.value.graphQLErrors ? updateClientPreferencesError.value.graphQLErrors[0].errorInfo.message : updateClientPreferencesError.value.errors.message
    }

    if (silent != null) return;

    await Swal.fire({
        title: "Success!",
        text: "Your preferences have been updated",
        icon: "success",
        confirmButtonColor: APP.MyWealthMode ? "#223F34" : "#13625a"
    });
}
